import React, { useEffect, useState } from 'react';
import { Image, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { ScopedSignupModal } from '@components/Modals/ScopedSignupModal.web';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import {
  Invitation,
  PublicTeam,
  useInvitationDetailsQuery,
} from '@graphql/generated';
import { usePublicTeamFromQuery } from '@hooks/usePublicTeamFromQuery';
import { useAuthContext } from '@src/context/authContext';
import theme from '@themes/theme';

import {
  getAvatarColor,
  getInitials,
} from '../Projects/projectInvitationPreviewShared';

/* ── types ───────────────────────────────────────────────────────────────── */

type AvatarFile = {
  cdnBaseUrl?: string | null;
  path?: string | null;
};

/* ── helpers ─────────────────────────────────────────────────────────────── */

const buildAvatarUri = (avatarFile?: AvatarFile | null): string | null =>
  avatarFile?.cdnBaseUrl && avatarFile?.path
    ? `${avatarFile.cdnBaseUrl}${avatarFile.path}`
    : null;

/* ── sub-components ──────────────────────────────────────────────────────── */

const SkeletonBlock: React.FC<{
  width?: number | string;
  height: number;
  style?: object;
}> = ({ width = '100%', height, style }) => (
  <Box
    backgroundColor='grey02'
    borderRadius='xs'
    style={[{ width, height } as any, style]}
  />
);

const LoadingSkeleton: React.FC = () => (
  <Box style={styles.contentWrapper}>
    <SkeletonBlock
      height={36}
      width={260}
      style={{ alignSelf: 'center', marginBottom: theme.spacing.l }}
    />
    <Box style={styles.teamCard}>
      <SkeletonBlock height={80} width={160} style={{ borderRadius: 8 }} />
      <SkeletonBlock
        height={32}
        width={200}
        style={{ marginTop: theme.spacing.s }}
      />
      <SkeletonBlock
        height={20}
        width={120}
        style={{ marginTop: theme.spacing.xs }}
      />
    </Box>
    <SkeletonBlock height={56} style={{ borderRadius: 8 }} />
  </Box>
);

/**
 * Team logo / initials box shared between both layouts.
 */
const TeamLogoBlock: React.FC<{
  name: string;
  avatarFile?: AvatarFile | null;
  logoStyle?: object;
}> = ({ name, avatarFile, logoStyle }) => {
  const [imgError, setImgError] = useState(false);
  const avatarUri = buildAvatarUri(avatarFile);
  const showImage = !!avatarUri && !imgError;

  return showImage ? (
    <Image
      source={{ uri: avatarUri }}
      style={[styles.teamLogo, logoStyle]}
      resizeMode='contain'
      onError={() => setImgError(true)}
    />
  ) : (
    <View
      style={[styles.teamInitialsBox, { backgroundColor: getAvatarColor(0) }]}>
      <Text style={styles.teamInitials}>{getInitials(name)}</Text>
    </View>
  );
};

/**
 * Team card used in the link-invite layout.
 */
const LinkInviteTeamCard: React.FC<{
  name: string;
  avatarFile?: AvatarFile | null;
  usersCount?: number | null;
}> = ({ name, avatarFile, usersCount }) => (
  <Box style={styles.teamCard}>
    <TeamLogoBlock name={name} avatarFile={avatarFile} />
    <Box alignItems='center' style={{ gap: theme.spacing.xs } as any}>
      <Text
        variant='heading1'
        color='textPrimary'
        textAlign='center'
        style={styles.teamName}>
        {name}
      </Text>
      <Box
        flexDirection='row'
        alignItems='center'
        style={{ gap: theme.spacing.xxs } as any}>
        <Icon name='Users' width={18} height={18} color='textSecondary' />
        <Text variant='body' color='textSecondary' style={{ fontSize: 18 }}>
          {(usersCount ?? 0) === 1
            ? '1 member on this team.'
            : `${usersCount ?? 0} members on this team.`}
        </Text>
      </Box>
    </Box>
  </Box>
);

/**
 * Hoverable CTA button — used in the link-invite layout.
 */
const CtaButton: React.FC<{
  label: string;
  onPress: () => void;
  disabled?: boolean;
  green?: boolean;
}> = ({ label, onPress, disabled = false, green = false }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole='button'
      accessibilityLabel={label}
      style={({ pressed }) => [
        styles.ctaButton,
        green && styles.ctaButtonGreen,
        disabled && styles.ctaButtonDisabled,
        !disabled && hovered && styles.ctaButtonHover,
        !disabled && pressed && styles.ctaButtonPressed,
      ]}>
      <Text
        style={[
          styles.ctaButtonLabel,
          disabled && styles.ctaButtonLabelDisabled,
        ]}>
        {label}
      </Text>
    </Pressable>
  );
};

/**
 * Pending approval badge shown when the user has already requested to join.
 */
const PendingBadge: React.FC = () => (
  <Box style={styles.pendingBadge}>
    <Icon name='Clock' width={16} height={16} color='textSecondary' />
    <Text variant='labelEmphasized' color='textSecondary' marginLeft='xs'>
      Request Pending Approval
    </Text>
  </Box>
);

/* ── main screen ─────────────────────────────────────────────────────────── */

const TeamInvitationPreviewScreenWeb: React.FC = () => {
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [hasJustRequested, setHasJustRequested] = useState(false);
  const { token: authToken } = useAuthContext();

  const invitationToken =
    (typeof window !== 'undefined'
      ? window.sessionStorage.getItem('invitation_token')
      : null) ?? '';

  const teamUUID =
    (typeof window !== 'undefined'
      ? (window.sessionStorage.getItem('team_preview_uuid') ??
        window.sessionStorage.getItem('task_or_project_id'))
      : null) ?? undefined;

  // Use the token path whenever we have an invitation_token, regardless of any
  // stale teamUUID in sessionStorage from a previous visit.
  const useTokenPath = !!invitationToken;

  const {
    data: inviteData,
    loading: inviteLoading,
    error: inviteError,
  } = useInvitationDetailsQuery({
    variables: { token: invitationToken, type: Invitation.Team },
    skip: !useTokenPath,
    fetchPolicy: 'network-only',
  });

  const inviteTeam = inviteData?.invitationDetails?.team ?? null;
  const invitationEmail =
    inviteData?.invitationDetails?.invitationEmail ?? null;
  const inviterName = inviteData?.invitationDetails?.invitedByName ?? '';
  const isEmailInvite = inviteData?.invitationDetails?.autoJoin === true;
  const isLinkInvite =
    inviteData?.invitationDetails?.isLinkInvite ?? !useTokenPath;
  // Link invites have no pre-assigned role (the invitation record's default role
  // is irrelevant — members join as Viewer pending admin approval).
  const inviteRole = isLinkInvite
    ? null
    : (inviteData?.invitationDetails?.invitationRole ?? 'Member');

  const persistInviteTeamSession = () => {
    if (!inviteTeam?.uuid) return;
    window.sessionStorage.setItem('team_preview_uuid', inviteTeam.uuid);
    window.sessionStorage.setItem('task_or_project_id', inviteTeam.uuid);
    // autoJoin=true means valid, non-expired email invite → accept token immediately.
    // autoJoin=false covers both shareable link tokens and expired email invites,
    // both of which fall through to the createUserRequest path.
    window.sessionStorage.setItem(
      'team_invite_auto_join',
      isEmailInvite ? '1' : '0'
    );
  };

  useEffect(() => {
    if (useTokenPath) {
      persistInviteTeamSession();
    }
  }, [useTokenPath, inviteTeam?.uuid, isEmailInvite]);

  const {
    publicTeam: uuidPublicTeam,
    loading: uuidLoading,
    error: uuidError,
  } = usePublicTeamFromQuery({
    teamUUId: useTokenPath ? undefined : teamUUID,
    fetchPolicy: 'network-only',
  });

  let publicTeam = uuidPublicTeam ?? null;
  if (useTokenPath) {
    publicTeam = inviteTeam
      ? ({
          name: inviteTeam.name,
          avatarFile: inviteTeam.avatarFile,
          createdAt: inviteTeam.createdAt,
          totalMembers: inviteTeam.totalMembers,
          hasPendingMemberAdditionRequest:
            inviteTeam.hasPendingMemberAdditionRequest,
          uuid: inviteTeam.uuid,
        } as PublicTeam)
      : null;
  }

  const loading = useTokenPath ? inviteLoading : uuidLoading;
  const error = useTokenPath ? inviteError : uuidError;
  const isInvalidToken = useTokenPath && !inviteLoading && !inviteTeam;

  const handleLogin = () => {
    // If the invitation query has already resolved, flush context to
    // sessionStorage before navigating so app-stack can resume correctly.
    // When the query hasn't resolved yet, app-stack's getInvitationDetails
    // fallback resolves the team UUID from the stored token.
    persistInviteTeamSession();
    window.location.href = `${window.location.origin}/log-in`;
  };

  const effectiveTeamUUID = inviteTeam?.uuid ?? teamUUID;
  const canSignUp = !!publicTeam && !!effectiveTeamUUID;

  /* ── top bar (link-invite layout) ────────────────────────────────────── */
  const renderTopBar = () => (
    <Box style={styles.topBar}>
      <Box style={styles.topBarInner}>
        <Icon name='Logo' width={100} height={28} color='textPrimary' />
        {authToken && (
          <Box style={styles.topBarAvatar}>
            <Icon name='User' width={18} height={18} color='grey05' />
          </Box>
        )}
        {!authToken && canSignUp && (
          <LoginLink label='Sign Up' onPress={() => setSignupModalOpen(true)} />
        )}
        {!authToken && !canSignUp && <LoginLink onPress={handleLogin} />}
      </Box>
    </Box>
  );

  /* ── loading ─────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <View style={styles.container}>
        {renderTopBar()}
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}>
          <LoadingSkeleton />
        </ScrollView>
      </View>
    );
  }

  /* ── error / not found ───────────────────────────────────────────────── */
  if (error || isInvalidToken || !publicTeam || !effectiveTeamUUID) {
    return (
      <View style={styles.container}>
        {renderTopBar()}
        <View style={styles.centeredState}>
          <Icon name='AlertTriangle' width={48} height={48} color='grey04' />
          <Text
            variant='heading2'
            color='textPrimary'
            textAlign='center'
            marginTop='m'
            marginBottom='s'>
            Team Not Found
          </Text>
          <Text
            variant='body'
            color='textSecondary'
            textAlign='center'
            marginBottom='xxl'
            style={{ maxWidth: 320 } as any}>
            This invitation link is invalid, expired, or the team no longer
            exists.
          </Text>
          <CtaButton label='Log In to TaskTag' onPress={handleLogin} />
        </View>
      </View>
    );
  }

  const hasPending = publicTeam.hasPendingMemberAdditionRequest;
  const usersCount = publicTeam.totalMembers;

  // Token was present but autoJoin=false and isLinkInvite=false → expired email invite
  const isExpiredEmailInvite = useTokenPath && !isEmailInvite && !isLinkInvite;

  /* ── email invite (valid, not expired): go directly to signup ────────── */
  if (isEmailInvite && !isLinkInvite) {
    return (
      <ScopedSignupModal
        invitationType='team'
        entityName={publicTeam.name}
        entityId={effectiveTeamUUID}
        isLinkInvite={false}
        isFullPage
        invitationToken={invitationToken}
        role={inviteRole ?? 'Member'}
        inviterName={inviterName}
        email={invitationEmail ?? ''}
        usersCount={usersCount}
        onClose={() => {}}
        onSuccess={() => {}}
      />
    );
  }

  /* ── expired email invite / shareable link: show "Request to Join" ────── */

  /* ── link invite layout ──────────────────────────────────────────────── */
  return (
    <View style={styles.container}>
      {renderTopBar()}

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}>
        <Box style={styles.contentWrapper}>
          {/* Expired email invite notice */}
          {isExpiredEmailInvite ? (
            <Box
              alignItems='center'
              marginBottom='l'
              style={{ gap: theme.spacing.s } as any}>
              <Box style={styles.expiredIconWrapper}>
                <Icon name='Clock' width={32} height={32} color='alertRed' />
              </Box>
              <Text
                variant='heading1'
                color='textPrimary'
                textAlign='center'
                style={styles.heading}>
                Invitation has expired
              </Text>
              <Text variant='body' color='textSecondary' textAlign='center'>
                You can still request to join this team.
              </Text>
            </Box>
          ) : (
            <Text
              variant='heading1'
              color='textPrimary'
              textAlign='center'
              marginBottom='l'
              style={styles.heading}>
              You're invited to join
            </Text>
          )}

          {/* Team card */}
          <LinkInviteTeamCard
            name={publicTeam.name}
            avatarFile={publicTeam.avatarFile}
            usersCount={usersCount}
          />

          {/* CTA */}
          {hasPending || hasJustRequested ? (
            <>
              <PendingBadge />
              <Text
                variant='metadata'
                color='textSecondary'
                textAlign='center'
                marginTop='s'
                marginBottom='l'>
                Your request has been sent. You'll be notified once a Team admin
                approves it.
              </Text>
            </>
          ) : (
            <>
              <CtaButton
                green
                label='Request to Join'
                onPress={() => setSignupModalOpen(true)}
              />
              <Text
                variant='metadata'
                color='textSecondary'
                textAlign='center'
                marginTop='s'
                marginBottom='l'>
                A Team admin will receive your request after sign up.
              </Text>
            </>
          )}

          {/* Login row */}
          <Text
            variant='metadata'
            color='textSecondary'
            textAlign='center'
            marginTop='l'>
            {'Already on TaskTag? '}
            <Text onPress={handleLogin} style={styles.loginLinkText}>
              Log In
            </Text>
          </Text>
        </Box>
      </ScrollView>

      {signupModalOpen && (
        <ScopedSignupModal
          invitationType='team'
          entityName={publicTeam.name}
          entityId={effectiveTeamUUID}
          isLinkInvite={true}
          invitationToken={invitationToken}
          role={inviteRole ?? 'Member'}
          inviterName={inviterName}
          email=''
          usersCount={usersCount}
          onClose={() => setSignupModalOpen(false)}
          onSuccess={() => {
            setSignupModalOpen(false);
            setHasJustRequested(true);
          }}
        />
      )}
    </View>
  );
};

/* ── small helpers ────────────────────────────────────────────────────────── */

const LoginLink: React.FC<{ onPress: () => void; label?: string }> = ({
  onPress,
  label = 'Log In',
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.loginLink,
        hovered && styles.loginLinkHover,
        { cursor: 'pointer' } as any,
      ]}>
      <Text
        style={[styles.loginLinkText, hovered && styles.loginLinkTextHover]}>
        {label}
      </Text>
    </Pressable>
  );
};

/* ── styles ──────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  // ── Shared ──
  container: {
    flex: 1,
    backgroundColor: theme.colors.grey01,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.l,
  },
  contentWrapper: {
    width: '100%' as any,
    maxWidth: 640,
  },
  heading: {
    fontSize: 32,
  },
  // ── Top bar ──
  topBar: {
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey02,
    position: 'sticky' as any,
    top: 0,
    zIndex: 10,
  },
  topBarInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: 640,
    width: '100%' as any,
    alignSelf: 'center' as any,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.s,
  },
  topBarAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: theme.colors.grey02,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // ── Link-invite team card ──
  teamCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadii.m,
    paddingHorizontal: theme.spacing.l,
    paddingVertical: theme.spacing.xxl,
    marginBottom: theme.spacing.l,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.06)' as any,
    gap: theme.spacing.xs,
  },
  teamLogo: {
    width: 160,
    height: 80,
    borderRadius: 8,
  },
  teamInitialsBox: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  teamInitials: {
    color: theme.colors.white,
    fontSize: 28,
    fontWeight: '700',
  },
  teamName: {
    fontSize: 32,
  },
  // ── CTA button ──
  ctaButton: {
    height: 56,
    borderRadius: 8,
    backgroundColor: theme.colors.textPrimary,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.xl,
    cursor: 'pointer' as any,
  },
  ctaButtonGreen: {
    backgroundColor: theme.colors.greenSecondary,
  },
  ctaButtonHover: {
    opacity: 0.88,
  },
  ctaButtonPressed: {
    opacity: 0.75,
    transform: [{ scale: 0.98 }],
  },
  ctaButtonDisabled: {
    backgroundColor: theme.colors.grey03,
    cursor: 'not-allowed' as any,
  },
  ctaButtonLabel: {
    color: theme.colors.white,
    fontWeight: '600',
  },
  ctaButtonLabelDisabled: {
    color: theme.colors.grey05,
  },
  // ── Expired notice ──
  expiredIconWrapper: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: theme.colors.alertRedMild,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // ── Pending badge ──
  pendingBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.grey02,
    borderRadius: theme.borderRadii.s,
    paddingVertical: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
    marginBottom: theme.spacing.xs,
    alignSelf: 'center' as any,
    gap: theme.spacing.xxs,
  },
  // ── Error / not-found state ──
  centeredState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.l,
    paddingVertical: theme.spacing.xxl,
    maxWidth: 480,
    alignSelf: 'center' as any,
  },
  // ── Login link ──
  loginLink: {},
  loginLinkHover: {
    opacity: 0.75,
  },
  loginLinkText: {
    color: theme.colors.greenSecondary,
    fontWeight: '600',
    fontSize: 13,
  },
  loginLinkTextHover: {
    textDecorationLine: 'underline',
  },
});

export default TeamInvitationPreviewScreenWeb;
